import React from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import { i18nReady } from './i18n';
import App from './App';
import { startAnimatedFavicon } from './utils/animatedFavicon';
import { isChunkLoadError, attemptChunkReload } from './utils/lazyRetry';
import { startVersionWatcher } from './versionWatcher';

startAnimatedFavicon();

// Global fix: prevent modals from closing when the user clicks inside
// modal content and accidentally drags/releases onto the overlay.
// Tracks where mousedown started; suppresses click/mousedown on overlay
// if the press originated inside (or very near) a modal child.
(() => {
  let mouseDownTarget = null;
  document.addEventListener('mousedown', (e) => {
    mouseDownTarget = e.target;
    // For onMouseDown handlers: suppress if press is near a modal child edge
    if (!e.target.classList.contains('modal-overlay')) return;
    const overlay = e.target;
    const modal = overlay.firstElementChild;
    if (!modal) return;
    const rect = modal.getBoundingClientRect();
    const m = 8;
    const x = e.clientX, y = e.clientY;
    if (x >= rect.left - m && x <= rect.right + m &&
        y >= rect.top - m && y <= rect.bottom + m) {
      e.stopImmediatePropagation();
    }
  }, true);
  // For onClick handlers: suppress click on overlay if mousedown started inside modal
  document.addEventListener('click', (e) => {
    if (!e.target.classList.contains('modal-overlay')) return;
    if (mouseDownTarget && !mouseDownTarget.classList.contains('modal-overlay')) {
      e.stopImmediatePropagation();
    }
  }, true);
})();

// Stale-tab ChunkLoadError guard.
// Every route is lazy-loaded (React.lazy → dynamic import of a hashed chunk).
// /static/* is cached `immutable` for 1y but index.html is not, so a tab left
// open from before a deploy holds the OLD index.html, which points at chunk
// hashes that no longer exist in the new build → "Loading chunk NNNN failed"
// the moment the user navigates to a not-yet-loaded route.
// Fix: on a chunk/dynamic-import load failure, reload to pull the fresh
// index.html + matching chunks. attemptChunkReload() is rolling-window guarded
// (shared with App.js ErrorBoundary), so it heals across EVERY deploy of the day
// — not just once per session — but still can't hard-loop on a broken build.
(() => {
  window.addEventListener('error', (e) => {
    if (isChunkLoadError(e?.error) || isChunkLoadError({ message: e?.message })) attemptChunkReload();
  });
  window.addEventListener('unhandledrejection', (e) => {
    if (isChunkLoadError(e?.reason)) attemptChunkReload();
  });
})();

const root = ReactDOM.createRoot(document.getElementById('root'));
i18nReady.then(() => root.render(<React.StrictMode><App /></React.StrictMode>));

// Register the service worker for Web Push (notifications when the helpdesk
// tab/browser is closed, and the only path to OS notifications on iPhone PWAs).
// Scope is global ('/'), production-only — local dev (localhost) keeps the SW
// out of the way to avoid stale-cache headaches during iteration.
//
// Las páginas públicas con token (el chat embebido en Oxígeno, la llamada y la asistencia
// del cliente) no lo registran: no hay sesión del helpdesk que notificar, y dentro del
// iframe de otra plataforma el registro fallaba con un 404 en la consola en cada carga.
const RUTA_PUBLICA_CON_TOKEN = /^\/(chat|llamada|assist)\//.test(window.location.pathname);
if ('serviceWorker' in navigator && window.location.hostname !== 'localhost' && !RUTA_PUBLICA_CON_TOKEN) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js').catch(err => {
      console.warn('[SW] registration failed:', err.message);
    });
  });
}

// Auto-update: recarga sola cuando se detecta un deploy nuevo (sin hard-refresh manual).
startVersionWatcher();
